// ── A PROVISIONAL PREVENT-PLANT FIGURE FROM THE 578, BEFORE THE AIP HAS KEYED IT ──
//
// Kevin, 2026-10-19: a 578 arrives BEFORE acres are keyed. If the farm has an OPEN
// prevent-plant claim for that crop year, the one PP engine (ppAdjuster.js adjudicateFarm)
// should be able to take the 578's prevented acres per crop and county as its PP-acre
// input — WHEN the AIP has not keyed acres yet — and say plainly that the figure is
// "provisional — from the 578, not yet keyed". Once the AIP's keyed acres exist, they win.
//
// This module does the substitution and nothing else. It does not price an acre (that is
// ppRate.js through the engine), does not decide eligibility (ppEligibility.js through
// the engine), and holds no copy of the prevent-plant stage codes (ppStages.js) or of the
// Active tab's status test (passed in). What it decides is narrow:
//
//   1. WHICH crops on which policies carry an OPEN prevent-plant claim — an active claim
//      whose stages include a prevent-plant code, or an outstanding Notice of Loss of
//      type PP (claimNotices.outstandingNotices, computed by the caller).
//   2. WHETHER THE AIP HAS KEYED THE CROP. Any base-plan unit of that crop and county with
//      acres on it means the acreage report is in, and its prevented lines are the answer —
//      including "none". A keyed zero beats a 578's 40 acres.
//   3. WHERE the 578's acres land. A cell is (county, crop, type) on one policy, and the 578
//      names neither the policy nor the unit. Every case it cannot place UNAMBIGUOUSLY is
//      HELD with the reason — never spread, never guessed:
//        - two policies both claim prevent plant on that crop and county;
//        - several types and the 578's intended use cannot split them (corn grain 016 /
//          silage 026 is the one split the 578 states);
//        - the cell's units carry different APH, level, share or practice — the 578 does not
//          say which unit its prevented field sits on, and pricing at one unit's terms is
//          the Krenzer defect (a single cell APH read $5,479 high).
//
// Admin-only by the caller. Nothing here writes, and nothing it produces is frozen.

import { isPreventPlantStage } from "./ppStages";
import { BASE_PLANS } from "./ppCells";

export const PROVISIONAL_LABEL = "Provisional — from the 578, not yet keyed";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();
// "Type not stated" has three spellings (ppCells.js): 997 / 000 / blank.
const tk = (t) => { const v = up(t); return (v === "997" || v === "000") ? "" : v; };
const tokens = (label) => String(label || "").split(",").map((x) => up(x)).filter(Boolean);
const isBase = (p) => BASE_PLANS.indexOf(String(p || "")) >= 0;

// (policyNumber|CROP) for every crop with an open prevent-plant claim this crop year.
//   claims        [{ policyNumber, cropYear, crop, status, stages }]  (indemnities, mapped)
//   outstanding   claimNotices.outstandingNotices(...) output — notices no claim shows yet
//   isActiveStatus  the Active tab's OWN status test, passed in (never re-typed here)
export function openPpClaimKeys({ claims = [], outstanding = [], cropYear, isActiveStatus } = {}) {
  if (typeof isActiveStatus !== "function") throw new Error("openPpClaimKeys needs the Active tab's status test");
  const year = Number(cropYear);
  const out = new Set();
  for (const c of claims || []) {
    if (!c || Number(c.cropYear) !== year || !isActiveStatus(c.status)) continue;
    const stages = String(c.stages || "").split(/[^A-Za-z0-9]+/).filter(Boolean);
    if (!stages.some((s) => isPreventPlantStage(s))) continue;
    for (const crop of tokens(c.crop)) out.add(`${up(c.policyNumber)}|${crop}`);
  }
  for (const n of outstanding || []) {
    if (!n || !n.crop) continue;
    const types = (n.noticeTypes || []).map(up);
    if (!types.includes("PP")) continue;
    out.add(`${up(n.policyNumber)}|${up(n.crop)}`);
  }
  return out;
}

// Has the AIP keyed any acreage for this farm's (county, crop)? Base plans only — an
// SCO/ECO row restates the same acres and says nothing on its own.
export function aipKeyedCountyCrops(units = []) {
  const keyed = new Set();
  for (const u of units || []) {
    if (!u || !isBase(u.plan)) continue;
    if (num(u.acres) == null) continue;
    keyed.add(`${up(u.county)}|${up(u.crop)}`);
  }
  return keyed;
}

// The 578's prevented acres per (county, crop), split by intended use where it states one.
function summaryByCountyCrop(ppSummary = []) {
  const m = new Map();
  for (const r of ppSummary || []) {
    const a = num(r && r.acres);
    if (!(a > 0)) continue;
    const k = `${up(r.county)}|${up(r.crop)}`;
    const e = m.get(k) || { county: up(r.county), crop: up(r.crop), total: 0, grain: 0, silage: 0, unknown: 0 };
    e.total += a;
    if (r.use === "grain") e.grain += a; else if (r.use === "silage") e.silage += a; else e.unknown += a;
    m.set(k, e);
  }
  for (const e of m.values()) for (const f of ["total", "grain", "silage", "unknown"]) e[f] = +e[f].toFixed(2);
  return m;
}

const CORN_TYPE_FOR_USE = { grain: "016", silage: "026" };

// groups: [{ policy: {id, policy_number}, cells, model }] exactly as adjudicateFarm takes.
// units:  the crop_coverage_units rows the cells were built from.
// Returns { groups, provisional, held, keyedWins } — `groups` is a NEW array; a cell
// given the 578's acres carries `provisional` so the engine's result says so.
export function applyProvisional578Pp(groups = [], { units = [], ppSummary = [], openPpKeys = new Set() } = {}) {
  const keyed = aipKeyedCountyCrops(units);
  const summary = summaryByCountyCrop(ppSummary);
  const provisional = [], held = [], keyedWins = [];
  const out = (groups || []).map((g) => ({ ...g, cells: (g.cells || []).map((c) => ({ ...c })) }));

  for (const s of summary.values()) {
    const ccKey = `${s.county}|${s.crop}`;
    const claimsCrop = (g) => openPpKeys.has(`${up(g.policy && g.policy.policy_number)}|${s.crop}`);
    if (!out.some(claimsCrop)) continue;                 // no open PP claim on this crop: not our case
    if (!s.county) {
      held.push({ county: "", crop: s.crop, acres: s.total, why: "the 578's prevented fields carry no county, so they cannot be matched to a coverage" });
      continue;
    }
    // Which policies hold this crop and county, and which of them have an open PP claim.
    const holders = out.filter((g) => g.cells.some((c) => `${up(c.county)}|${up(c.crop)}` === ccKey));
    const claiming = holders.filter(claimsCrop);
    if (!claiming.length) {
      held.push({ county: s.county, crop: s.crop, acres: s.total,
        why: "no policy with an open prevent-plant claim on this crop covers this county" });
      continue;
    }
    if (keyed.has(ccKey)) { keyedWins.push({ county: s.county, crop: s.crop, acres578: s.total }); continue; }
    if (claiming.length > 1) {
      held.push({ county: s.county, crop: s.crop, acres: s.total,
        why: "more than one of this farm's policies has an open prevent-plant claim on this crop and county, and the 578 does not say which policy its fields are on" });
      continue;
    }
    const g = claiming[0];
    const cells = g.cells.filter((c) => `${up(c.county)}|${up(c.crop)}` === ccKey);
    // Split across types only where the 578 states the split (corn grain vs silage).
    let parts;
    if (cells.length === 1) parts = [{ cell: cells[0], acres: s.total }];
    else if (s.crop === "CORN" && s.unknown < 0.005) {
      parts = [];
      for (const use of ["grain", "silage"]) {
        if (!(s[use] > 0.005)) continue;
        const c = cells.find((x) => tk(x.type) === CORN_TYPE_FOR_USE[use]);
        if (!c) { parts = null; break; }
        parts.push({ cell: c, acres: s[use] });
      }
    } else parts = null;
    if (!parts) {
      held.push({ county: s.county, crop: s.crop, acres: s.total,
        why: "this crop is insured under more than one type here and the 578's intended use does not say which type its prevented acres are" });
      continue;
    }
    for (const { cell, acres } of parts) {
      const terms = new Map();
      for (const u of units || []) {
        if (!isBase(u.plan) || u.policy_id !== g.policy.id) continue;
        if (up(u.county) !== up(cell.county) || up(u.crop) !== up(cell.crop) || tk(u.type_code) !== tk(cell.type)) continue;
        const t = { aph: num(u.aph_yield), coverageLevel: num(u.coverage_level), share: num(u.share), practice: u.practice_code == null ? null : String(u.practice_code) };
        terms.set(JSON.stringify(t), t);
      }
      if (terms.size !== 1) {
        held.push({ county: s.county, crop: s.crop, type: cell.type || null, acres,
          why: terms.size === 0
            ? "no insured unit is on file for this crop and county to price the 578's prevented acres against"
            : "the units under this crop carry different APH, coverage level, share or practice, and the 578 does not say which unit its prevented fields sit on" });
        continue;
      }
      const t = [...terms.values()][0];
      cell.ppAcres = +acres.toFixed(2);
      cell.ppUnits = [{ acres: cell.ppAcres, aph: t.aph, coverageLevel: t.coverageLevel, share: t.share,
        status: null, practice: t.practice }];
      cell.provisional = { source: "578", label: PROVISIONAL_LABEL, acres: cell.ppAcres };
      provisional.push({ policyId: g.policy.id, policyNumber: g.policy.policy_number, county: s.county, crop: s.crop, type: cell.type || null, acres: cell.ppAcres });
    }
  }
  return { groups: out, provisional, held, keyedWins };
}
